/**
 * DismissWorkflowButton
 * The always-visible, labelled "Dismiss" action for a top-level workflow (JO-07). Shared by the
 * panel rows, the rail cards and the dashboard Pending Workflows card so the control, its touch
 * target and its in-flight state are defined once. Dismissal is sticky and has no restore
 * affordance by product decision (see WorkflowPanelProvider).
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';

interface DismissWorkflowButtonProps {
  workflowId: string;
  /** Accessible name; starts with the visible text "Dismiss" and identifies the workflow. */
  accessibleLabel: string;
  testId: string;
  onDismiss: (id: string) => Promise<void>;
  className?: string;
}

export function DismissWorkflowButton({
  workflowId,
  accessibleLabel,
  testId,
  onDismiss,
  className,
}: DismissWorkflowButtonProps) {
  const [isDismissing, setIsDismissing] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    // The control sits beside a row's drill-in button; never trigger it.
    e.stopPropagation();
    setIsDismissing(true);
    setFailed(false);
    try {
      await onDismiss(workflowId);
    } catch {
      // Surface the failure inline (the landlord app has no toast system) and re-enable below so
      // the landlord can retry; never let it escape the click handler as an unhandled rejection.
      setFailed(true);
    } finally {
      setIsDismissing(false);
    }
  };

  return (
    <div className="flex shrink-0 flex-col items-end">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={(e) => void handleClick(e)}
        disabled={isDismissing}
        data-testid={testId}
        aria-label={accessibleLabel}
        className={cn('min-h-11 shrink-0 px-3 text-muted-foreground hover:text-foreground', className)}
      >
        Dismiss
      </Button>
      {failed && (
        <p role="alert" data-testid={`${testId}-error`} className="text-xs text-destructive">
          Couldn&apos;t dismiss. Try again.
        </p>
      )}
    </div>
  );
}
